'use client';

import { useEffect } from 'react';

/**
 * Two production-crash guards:
 *
 * 1. Google Translate (and similar DOM-rewriting extensions) detaches text
 *    nodes React still owns — the next commit throws NotFoundError on
 *    removeChild/insertBefore and takes the whole tree down. 104 crashes /
 *    41 users in the last 7 days. The React-team-endorsed workaround: fail
 *    those calls soft instead of crashing.
 *    https://martijnhols.nl/blog/everything-about-google-translate-crashing-react
 *
 * 2. Deploys invalidate hashed chunks for already-open tabs (ChunkLoadError).
 *    One automatic reload picks up the new manifest; a session flag prevents
 *    reload loops.
 */
export default function DomSafetyGuard() {
  useEffect(() => {
    // DOM patches moved to an inline beforeInteractive script in the root
    // layout (this effect ran post-hydration and left a crash window).

    const onChunkError = (event: ErrorEvent) => {
      const msg = event?.message || '';
      if (/Loading chunk .* failed|ChunkLoadError/i.test(msg)) {
        const KEY = 'stx_chunk_reloaded';
        if (!sessionStorage.getItem(KEY)) {
          sessionStorage.setItem(KEY, '1');
          window.location.reload();
        }
      }
    };
    window.addEventListener('error', onChunkError);
    return () => window.removeEventListener('error', onChunkError);
  }, []);

  return null;
}
